import * as Sentry from "@sentry/react";
import { Component, ErrorInfo, ReactElement } from "react";

import { getAppConfig } from "../config/get-app-config.ts";
import { routes } from "../stores/router.ts";
import { Button } from "./catalyst/Button.tsx";

type Props = {
  children: ReactElement;
};

type State = {
  error: Error | null;
  hasError: boolean;
};

class ErrorBoundary extends Component<Props> {
  state: State = {
    error: null,
    hasError: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = { error: null, hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error, hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Error caught by boundary:", error, info);

    if (getAppConfig().envMode === "production") {
      Sentry.captureException(error, {
        extra: {
          componentStack: info.componentStack,
          message: error.message,
          name: error.name,
        },
      });
    }
  }

  render(): ReactElement {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-100">
          <div className="w-96 rounded-lg bg-white p-8 text-center shadow-md">
            <h1 className="mb-2 text-2xl font-bold">Something went wrong</h1>

            <Button
              href={routes.dashboard}
              onClick={() => (window.location.href = routes.dashboard)}
            >
              Dashboard
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
